"use client";

import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function Services() {
  const header = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const disciplines = [{
    numeral: "I.",
    title: "OTA Revenue Management",
    french: "Strategic Channel Optimization",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_revenue_management.webp",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_revenue_management.webp-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/ota_revenue_management.webp-wvc-sizes",
    alt: "Data-driven dashboard showing OTA revenue growth metrics",
    paragraphs: ["We transform your OTA presence into a high-performing revenue engine. By leveraging deep data analytics, we optimize your listings to capture maximum demand while maintaining rate integrity across all platforms.", "Sunil Singh’s methodology focuses on visibility, conversion, and competitive positioning. We don't just manage listings; we engineer them to outperform your local competition consistently.", "Our team continuously monitors market shifts, adjusting your OTA strategy in real-time to ensure your property remains the top choice for travelers, driving higher occupancy and RevPAR."],
    technique: "Data-Driven Strategy",
    context: "Global OTA Platforms",
    duration: "Ongoing Support",
    materials: "Analytics, Market Data"
  }, {
    numeral: "II.",
    title: "Hospitality Tech Implementation",
    french: "Tech Stack Optimization",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hospitality_tech_stack.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hospitality_tech_stack.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/hospitality_tech_stack.png-wvc-sizes",
    alt: "Modern hotel technology stack integration interface",
    paragraphs: ["Your tech stack is the backbone of your revenue. We audit, recommend, and implement the right tools—from Channel Managers to PMS and Booking Engines—to streamline your operations.", "We bridge the gap between complex technology and hotel revenue goals. Our implementation process ensures seamless integration, reducing manual errors and freeing your team to focus on guest experience.", "By selecting the right software ecosystem, we help you automate pricing, centralize inventory, and gain actionable insights that directly impact your bottom line."],
    technique: "System Integration",
    context: "PMS & Channel Managers",
    duration: "4–8 weeks",
    materials: "Software, API Tools"
  }, {
    numeral: "III.",
    title: "Revenue Audits",
    french: "Performance Analysis",
    image: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png",
    srcset: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png-wvc-srcset",
    sizes: "https://wpvc-images.s3.us-east-1.amazonaws.com/images/1698330/img/revenue_audit_analysis.png-wvc-sizes",
    alt: "Professional revenue audit report on a tablet",
    paragraphs: ["A comprehensive revenue audit is the first step toward unlocking hidden potential. We analyze your historical data, pricing strategies, and market positioning to identify missed opportunities.", "Our audits provide a clear roadmap for growth. We uncover inefficiencies in your current revenue management practices and provide actionable recommendations to boost your profitability.", "Whether you are a boutique hotel or a large resort, our audit process is tailored to your specific market challenges, ensuring you have the clarity needed to make informed, data-backed decisions."],
    technique: "Deep Data Audit",
    context: "Historical Performance",
    duration: "2–4 weeks",
    materials: "Audit Reports, KPIs"
  }];
  return <section data-section-id="3027" id="servicenavigation" className="bg-slate-50 text-slate-900 font-default py-32 md:py-40 px-6 md:px-10">
      <div ref={header.ref} className={`max-w-3xl mx-auto text-center transition-all duration-1000 ease-out ${header.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
        <div className="text-2xl text-blue-600 mb-8" aria-hidden="true" style={{
        letterSpacing: "0.1em"
      }}>
          &#9670;
        </div>
        <p className="font-sans text-xs uppercase mb-10 text-slate-600" style={{
        letterSpacing: "0.3em"
      }}>
          &mdash; Expertise &middot; Our Services
        </p>
        <h2 className="font-default text-5xl md:text-6xl lg:text-7xl leading-[1.05] tracking-tight text-slate-900">
          Data-Driven
          <br />
          Revenue
          <br />
          <span className="italic text-blue-600">Growth Strategies.</span>
        </h2>
        <p className="mt-12 max-w-xl mx-auto text-base md:text-lg leading-relaxed text-slate-700">
          We provide specialized revenue management consulting designed to maximize your hotel's profitability through proven OTA strategies, technology integration, and rigorous performance audits.
        </p>
      </div>

      <div className="mt-32 md:mt-40 max-w-7xl mx-auto">
        {disciplines.map((d, i) => {
        const reversed = i % 2 === 1;
        const {
          ref,
          isVisible
        } = useRevealOnScroll<HTMLDivElement>({
          threshold: 0.12
        });
        return <div key={i} data-index={i}>
              <div ref={ref} className={`grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-20 items-center transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-10"}`}>
                <div className={`relative ${reversed ? "lg:order-2" : "lg:order-1"}`}>
                  <div className="relative">
                    <div className="absolute -bottom-3 -right-3 md:-bottom-4 md:-right-4 w-full h-full border border-blue-600/70" aria-hidden="true"></div>
                    <div className="relative overflow-hidden bg-slate-200 rounded-lg shadow-lg">
                      <img src={d.image} data-wvc-srcset={d.srcset} data-wvc-sizes={d.sizes} alt={d.alt} loading="lazy" decoding="async" className="w-full h-full object-cover aspect-[5/6]" />
                    </div>
                  </div>
                </div>

                <div className={reversed ? "lg:order-1" : "lg:order-2"}>
                  <div className="font-default italic text-blue-600 text-6xl md:text-7xl lg:text-[80px] leading-none mb-6" aria-hidden="true">
                    {d.numeral}
                  </div>
                  <h3 className="font-default text-3xl md:text-4xl lg:text-[44px] leading-tight text-slate-900">
                    {d.title}
                  </h3>
                  <p className="font-default italic text-base text-slate-600 mt-2">
                    {d.french}
                  </p>

                  <div className="mt-8 space-y-5">
                    {d.paragraphs.map((p, pi) => <p data-index={pi} key={pi} className="text-base md:text-[17px] leading-[1.8] text-slate-800">
                        {p}
                      </p>)}
                  </div>

                  <div className="mt-12 border-t border-slate-200 pt-8">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-x-6 gap-y-6">
                      {[{
                    label: "Technique",
                    value: d.technique
                  }, {
                    label: "Focus Area",
                    value: d.context
                  }, {
                    label: "Typical Duration",
                    value: d.duration
                  }, {
                    label: "Key Assets",
                    value: d.materials
                  }].map((spec, si) => <div data-index={si} key={si} className="md:border-r md:last:border-r-0 border-slate-200 md:pr-6">
                          <p className="font-sans text-[10px] uppercase text-slate-500 mb-2" style={{
                      letterSpacing: "0.2em"
                    }}>
                            {spec.label}
                          </p>
                          <p className="font-default text-sm md:text-base text-slate-900 leading-snug">
                            {spec.value}
                          </p>
                        </div>)}
                    </div>
                  </div>
                </div>
              </div>

              {i < disciplines.length - 1 && <div className="flex items-center justify-center my-24 md:my-32" aria-hidden="true">
                  <span className="h-px flex-1 bg-slate-200/70"></span>
                  <span className="px-6 text-blue-600 text-lg">&#9670;</span>
                  <span className="h-px flex-1 bg-slate-200/70"></span>
                </div>}
            </div>;
      })}
      </div>
    </section>;
}